/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { DataApiService } from './data-api-service';
import { ScoreTimeSeriesService } from './score-time-series.service';
import { ScoreUtilsService } from '../../utils/score-utils.service';
import { AuthService } from 'src/app/service/auth.service';
import { EntityInfoService } from '../metadata-api/entity-info.service';
import { DateUtils } from '../../utils/date.utils';
import { ScoreTimeSeriesResponse } from '../../models/score-time-series.interface';

export interface PortfolioEntity {
  compound_key: string;
  type: string;
  alpha_id: string;
  name: string;
  [key: string]: any;
}

export interface PortfolioItemData {
  entity: PortfolioEntity;
  sentiment: number | null;
  sentiment_delta: number | null;
  count: number;
  volume_ratio: number | null;
  score?: number | null;
  trend_type?: 'bull' | 'bear';
  trend_value?: string;
}

export interface PortfolioTreemap {
  treemap: {
    children: PortfolioItemData[];
  };
}

export interface PortfolioDataParams {
  time?: string;
  period?: string;
  getSanctions?: boolean;
  [key: string]: any;
}

interface StatsData {
  count: number;
  volume_ratio: number | null;
  sentiment: number | null;
  sentiment_delta: number | null;
}

interface ScoreData {
  score: number | null;
  trend_type?: 'bull' | 'bear';
  trend_value?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PortfolioDataService {
  private readonly filterParams = [
    'from',
    'to',
    'lang',
    'feed',
    'categories',
    'continents',
    'countries',
    'factuality',
    'temporality',
    'ranks',
    'panels',
  ];

  constructor(
    private dataApiService: DataApiService,
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    private auth: AuthService,
    private entityInfoService: EntityInfoService,
  ) {}

  getPortfolioData(
    params: PortfolioDataParams,
    folioContent: string[],
  ): Observable<PortfolioTreemap> {
    if (!folioContent || folioContent.length === 0) {
      return of({ treemap: { children: [] } });
    }

    return this.entityInfoService
      .getEntitiesInfo(folioContent, 'array', params.getSanctions || false)
      .pipe(
        mergeMap((entities) => {
          const portfolioEntities = entities as PortfolioEntity[];
          const compoundKeys = portfolioEntities.map((e) => e.compound_key);

          return forkJoin({
            stats: this.fetchStats(params, compoundKeys),
            scores: this.fetchScores(compoundKeys, params.time || '7'),
          }).pipe(
            map(({ stats, scores }) =>
              this.buildTreemap(portfolioEntities, stats, scores),
            ),
          );
        }),
        catchError((error) => {
          console.error('Portfolio data fetch error:', error);
          return of({ treemap: { children: [] } });
        }),
      );
  }

  private fetchStats(
    params: PortfolioDataParams,
    compoundKeys: string[],
  ): Observable<Map<string, StatsData>> {
    const requestParams: any = {};

    if (params.time && !params.from && !params.to) {
      const fromTo = DateUtils.fromTo(params.time);
      if (fromTo) {
        requestParams.from = fromTo.dateFrom;
        requestParams.to = fromTo.dateTo;
        const timeframeLength = fromTo.period || 7;
        requestParams.period =
          timeframeLength <= 7 ? 1 : timeframeLength <= 30 ? 7 : 30;
      }
    }

    this.filterParams.forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        requestParams[key] = value;
      }
    });

    return this.dataApiService
      .post<any>(
        'portfolio/sentiment_itemized',
        compoundKeys,
        requestParams,
        'v2',
      )
      .pipe(
        map((response) => {
          const statsMap = new Map<string, StatsData>();

          response?.sentiment_itemized?.forEach((item: any) => {
            const key = item.entity?.compound_key;
            if (key && item.sentiment) {
              statsMap.set(key, {
                count: item.sentiment.count || 0,
                volume_ratio: item.sentiment.volume_ratio ?? null,
                sentiment: item.sentiment.sentiment ?? null,
                sentiment_delta: item.sentiment.sentiment_delta ?? null,
              });
            }
          });

          return statsMap;
        }),
        catchError(() => of(new Map())),
      );
  }

  private fetchScores(
    compoundKeys: string[],
    time: string,
  ): Observable<Map<string, ScoreData>> {
    const defaultScore = this.auth.scorelabSettings.defaultScore;

    if (defaultScore === 'sentiment') {
      return of(new Map());
    }

    const isBullBear = defaultScore === 'bb';
    const scoreType = this.scoreUtils.resolveScoreType(defaultScore);

    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {},
        body: {
          score_type: scoreType,
          entity: compoundKeys,
          time: time,
        },
      })
      .pipe(
        map((response: ScoreTimeSeriesResponse) => {
          const scoresMap = new Map<string, ScoreData>();

          response.score_ts?.forEach((item) => {
            const key = item.entity_info?.compound_key;
            if (!key) return;

            const timeSeries = item.score_ts || [];
            const lastPoint = timeSeries[timeSeries.length - 1];
            const scoreValue = lastPoint?.score ?? null;

            const result: ScoreData = { score: scoreValue };

            if (isBullBear && scoreValue != null) {
              result.trend_type = scoreValue > 5.0 ? 'bull' : 'bear';
              result.trend_value = scoreValue.toFixed(1);
            }

            scoresMap.set(key, result);
          });

          return scoresMap;
        }),
        catchError(() => of(new Map())),
      );
  }

  private buildTreemap(
    entities: PortfolioEntity[],
    stats: Map<string, StatsData>,
    scores: Map<string, ScoreData>,
  ): PortfolioTreemap {
    const children: PortfolioItemData[] = entities.map((entity) => {
      const entityStats = stats.get(entity.compound_key) || {
        count: 0,
        volume_ratio: null,
        sentiment: null,
        sentiment_delta: null,
      };
      const entityScores = scores.get(entity.compound_key) || { score: null };

      return {
        entity,
        sentiment: entityStats.sentiment,
        sentiment_delta: entityStats.sentiment_delta,
        count: entityStats.count,
        volume_ratio: entityStats.volume_ratio,
        score: entityScores.score,
        trend_type: entityScores.trend_type,
        trend_value: entityScores.trend_value,
      };
    });

    return { treemap: { children } };
  }
}
